import { Checkbox, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { DropdownRules } from "../../types/field";
import { GetRequest } from "../../utils/api";
import fuzzySearch from "../../utils/fuzzySearch";

type Props = {
  optionList: Array<any>;
  title: string;
  placeholder: string;
  onChange?: any;
  disabled?: boolean;
  data?: any;
  className?: string;
  dropdownCode?: string;
  dropdownValue?: string;
  enableSearch?: boolean;
  mode?: any;
  allowClear?: boolean;
  loading: boolean;
  rules?: DropdownRules;
  control: any;
  name: string;
  setValue?: any;
  disabledOption?: any;
  setOptionValueList?: any;
  optionValueList?: any;
  value?: any;
  showOnlyValue?: boolean;
  maxTagCount?: number;
};
const { Option } = Select;

const DropDown: React.FC<Props> = ({
  title,
  optionList,
  loading,
  data,
  onChange,
  placeholder,
  disabled,
  dropdownCode = "code",
  dropdownValue = "name",
  enableSearch,
  allowClear,
  mode,
  rules,
  control,
  name,
  setValue,
  disabledOption,
  setOptionValueList,
  optionValueList,
  value,
  showOnlyValue,
  maxTagCount = 1
}) => {

  let dropDownMode = rules?.isMulti ? "multiple" : mode;

  //Fuzzy Search - only
  useEffect(() => {

    // Fuzzy search is triggered when a value is set to Dropdown but it is not present in optionList 
    // This case will arise only when the value is set from code and not from the dropdown

    //Check if the value is in the option
    let flag = optionList.some((item) => item[dropdownCode] && (item[dropdownCode] === value));

    //If mode is miltile - check every value in the array
    if (dropDownMode === 'multiple' && value && value.length > 0 && typeof value !== "string") {

      flag = true;

      value.forEach((val: any) => {

        if (!optionList.some((item) => item[dropdownCode] && (item[dropdownCode] === val))) {
          flag = false;
        }

      });

    }


    if (!flag) {

      if (value) {

        if (dropDownMode === 'multiple') {

          for (let i = 0; i < value.length; i++) {

            const temp = fuzzySearch(optionList, dropdownValue, value[i].toString());

            if (temp.length > 0) {

              const tempVal = temp[0][dropdownCode];

              if (setValue) {
                setValue(name, [value, ...tempVal]);
              }

              onChange([value, ...tempVal]);

            }

          }

        } else {

          const temp = fuzzySearch(optionList, dropdownValue, value.toString());

          if (temp.length > 0) {

            const tempVal = temp[0][dropdownCode];

            if (setValue) {
              setValue(name, tempVal);
            }

            onChange(tempVal);
          }
        }
      }
    }
  }, [value]);

  const filtered = useMemo(() => {
    let result = [...optionList];

    if (dropdownCode === 'paymentNatureCode') {
      setOptionValueList([...optionList])
    }

    return result;
  }, [dropdownCode, dropdownValue, optionList]);


  const onFilterOptions = (input: string, option: any) => {

    return (option?.name ?? "")
      .toLowerCase()
      .includes(input.toLowerCase());
  }

  return (

    <>
      <Select
        style={{ minWidth: "140px", width: "100%" }}
        menuItemSelectedIcon={null}
        mode={dropDownMode}
        placeholder={"Select " + title}
        disabled={disabled}
        loading={loading}
        showSearch={enableSearch}
        allowClear={true}
        filterOption={onFilterOptions}
        onChange={(value: any) => {

          onChange(value);
        }}
        maxTagCount={maxTagCount}
        value={value}
      >
        {filtered.map((item: any, idx: number) => (

          <Option
            title={item?.[dropdownValue || "name"]}
            key={idx}
            value={item?.[dropdownCode ? dropdownCode : "code"]}
            disabled={dropdownCode === 'paymentNatureCode' ? disabledOption[idx] : false}
            name={item?.[dropdownValue] + "-" + item?.[dropdownCode]}
            className="notranslate"
          >
            {rules?.isMulti && <Checkbox className="mr-2" checked={value?.includes(item?.[dropdownCode ? dropdownCode : "code"])} onChange={(ev) => { ev.preventDefault(); ev.stopPropagation(); }}>
            </Checkbox>}

            {showOnlyValue ? item?.[dropdownValue] : item?.[dropdownCode] + "-" + item?.[dropdownValue]}


          </Option>

        ))}
      </Select>
    </>

  );
};

// wrapper component for data fetching... ideally should be done from caller side.
type CustomDropDownProps = {
  title: string;
  placeholder: string;
  onChange?: any;
  disabled?: boolean;
  data?: any;
  className?: string;
  api: string;
  dropdownCode?: string;
  dropdownValue?: string;
  enableSearch?: boolean;
  mode?: any;
  allowClear?: boolean;
  rules?: DropdownRules;
  control: any;
  name: string;
  setValue?: any;
  disabledOption?: any;
  setOptionValueList?: any;
  optionValueList?: any;
  value?: string;
  showOnlyValue?: boolean;
  staticDropDownValues?: [];
  maxTagCount?: number;
};
const CustomDropDown: React.FC<CustomDropDownProps> = ({
  title,
  data,
  onChange,
  api,
  placeholder,
  disabled = false,
  dropdownCode,
  dropdownValue,
  enableSearch = true,
  allowClear = false,
  mode = undefined,
  rules,
  control,
  name,
  setValue,
  disabledOption,
  setOptionValueList,
  optionValueList,
  value,
  showOnlyValue,
  staticDropDownValues,
  maxTagCount
}) => {
  const [optionList, setOptionList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState();

  if (typeof data === "string" && data.length === 0) {
    data = undefined;
  } else if (value === null) {
    data = undefined;
  }
  //Todo: move api call to caller and just pass data and remove useeffect.
  const getOptionList = () => {
    setLoading(true);

    if (api?.length > 0) {

      GetRequest(api).then((result: any) => {
        setOptionList(result.records);
        setLoading(false);
      }).catch((error: any) => {
        setLoading(false);
        console.log("error", error);
      });

    } else {
      setLoading(false);
    }

  };

  useEffect(() => {

    setSelected(data);

  }, [data]);

  useEffect(() => {
    if (api) {
      getOptionList();
    } else if (staticDropDownValues && staticDropDownValues.length > 0) {
      setOptionList([...staticDropDownValues]);
    }
  }, [api, staticDropDownValues?.length]);

  return (
    <DropDown
      optionList={optionList}
      loading={loading}
      data={selected}
      value={selected}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      dropdownCode={dropdownCode}
      dropdownValue={dropdownValue}
      enableSearch={enableSearch}
      allowClear={allowClear}
      mode={mode}
      rules={rules}
      control={control}
      name={name}
      title={title}
      setValue={setValue}
      disabledOption={disabledOption}
      setOptionValueList={setOptionValueList}
      optionValueList={setOptionValueList}
      showOnlyValue={showOnlyValue}
      maxTagCount={maxTagCount}
    />
  );
};

export default CustomDropDown;
